/* © 2018-2022 TakuLabs Ltd. All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential */

import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, LOCALE_ID, inject } from "@angular/core";
import { ValidatorFn, Validators } from "@angular/forms";
import { Col, DataType, FilterType } from "../../../../form-list/form-list/form-list";
import { FormListModel } from "../../../../form-list/form-list/form-list-model.interface";
import { CurrencySymbolPipe } from "../../../../shared/pipes/currency-symbol.pipe";
import { AuthService } from "../../../../shared/services/auth.service";
import { DBService } from "../../../../shared/services/db.service";
import { InventoryService } from "../../../inventory/inventory/inventory.service";
import { InventoryDocLine } from "./inventory-document";
import { PackSize } from "src/app/core/inventory/inventory-supplier-product-detail/inventory-supplier-product-detail";
import { DiscountType } from "../../sale-document/sale-doc/sale-doc";
import { DataNormalizerService } from "src/app/shared/services/data-normalizes.service";
import { MonetaryHelpers } from "src/app/utility/MonetaryHelpers";

@Injectable({
  providedIn: "root",
})
export class InventoryDocLineService extends DBService implements FormListModel {
  enum_pack_size = this.enumSelectOptions(PackSize);
  normalizerService = inject(DataNormalizerService);

  constructor(
    @Inject(LOCALE_ID) private defaultLocale: string,
    protected http: HttpClient,
    protected authService: AuthService,
    private inventoryService: InventoryService
  ) {
    super(http, authService);
  }

  getValidationRules(): { [key: string]: ValidatorFn[] | {} } {
    return {
      convRate: [Validators.required],
      unitCost: [Validators.required],
    };
  }

  moneyPrefix(rowData: InventoryDocLine) {
    return rowData.docReference.doc.currencyIsoCode;
  }

  zoneMoneyPrefix(rowData: InventoryDocLine) {
    return rowData.docReference.zone.defaultCurrencyIsoCode;
  }

  discountPrefix(rowData: InventoryDocLine) {
    if (rowData.allocatedDiscountType == DiscountType.Fixed) {
      return new CurrencySymbolPipe(this.defaultLocale).transform(rowData.docReference.doc.currencyIsoCode);
    } else return "";
  }

  discountSuffix(rowData: InventoryDocLine) {
    if (rowData.allocatedDiscountType == DiscountType.Percentage) {
      return " " + MonetaryHelpers.roundToDecimalPlaces(rowData.allocatedDiscountAmount) + "%";
    } else return "";
  }

  getFormListColumns(extraData?: any): Col[] {
    const cols: Col[] = [];
    if (extraData && extraData.skuTemplateOptions)
      cols.push({
        field: "docLine.inventory.sku",
        header: "SKU",
        visible: true,
        readonly: true,
        frozen: false,
        dataType: DataType.template,
        dataOptions: [],
        dataTypeOptions: extraData.skuTemplateOptions,
        filterType: FilterType.contains,
        filterMatchMode: FilterType.contains,
        isNotSortable: true,
      });

    cols.push(
      ...[
        // supplier sku
        {
          field: "supplierCode",
          header: "Supplier Code",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.input,
          dataOptions: [],
          // dataTypeOptions: extraData.skuTemplateOptions,
          filterType: FilterType.none,
          isNotSortable: true,
        },
        {
          field: "docLine.inventory.description1",
          header: "Product Title",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.input,
          dataOptions: [],
          filterType: FilterType.contains,
          isNotSortable: true,
        },
        {
          field: "inventoryVariantsStr",
          header: "Variant",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.input,
          dataOptions: [],
          dataTypeOptions: DataType.input,
          filterType: FilterType.contains,
          colWidth: 140,
          isNotSortable: true,
          filterMatchMode: FilterType.contains,
        },
        {
          field: "docLine.inventory.uOfMeasureId",
          header: "Default U of M",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.lookup,
          dataOptions: this.inventoryService.get_lookup_uOfMeasure(),
          filterType: FilterType.lookup,
          sortFields: ["UOfMeasure.name"],
          isNotSortable: true,
          colWidth: 110,
        },
        // {
        //   field: "uOfMeasureId",
        //   header: "Current U of M",
        //   visible: true,
        //   readonly: true,
        //   frozen: false,
        //   dataType: DataType.lookup,
        //   dataOptions: this.inventoryService.get_lookup_uOfMeasure(),
        //   filterType: FilterType.lookup,
        //   sortFields: ["UOfMeasure.name"],
        // },
        {
          field: "convRate",
          header: "Conversion Rate",
          visible: true,
          readonly: false,
          frozen: false,
          dataType: DataType.input,
          dataOptions: [],
          filterType: FilterType.contains,
          isNotSortable: true,
          colWidth: 140,
        },
        //
        {
          field: "packSize",
          header: "Pack Size",
          visible: true,
          readonly: false,
          frozen: false,
          dataType: DataType.enum,
          dataOptions: this.enum_pack_size,
          filterType: FilterType.enum,
          isNotSortable: true,
          colWidth: 110,
        },
        // on order
        {
          field: "qty",
          header: "Received Qty",
          visible: true,
          readonly: false,
          frozen: false,
          dataType: DataType.number,
          dataOptions: [],
          filterType: FilterType.contains,
          isNotSortable: true,
        },
        {
          field: "skuQtyPerPack",
          header: "SKU Qty / Pack",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.number,
          dataOptions: [],
          filterType: FilterType.contains,
          isNotSortable: true,
          displayFn: (rowData: InventoryDocLine) => {
            return this.normalizerService.currencyNormalizer(
              rowData.skuQtyPerPack,
              rowData.docReference.doc.currencyIsoCode
            );
          },
        },
        {
          field: "totalQty",
          header: "Total Qty Received",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.number,
          dataOptions: [],
          filterType: FilterType.contains,
          isNotSortable: true,
          displayFn: (rowData: InventoryDocLine) => {
            return this.normalizerService.currencyNormalizer(
              rowData.totalQty,
              rowData.docReference.doc.currencyIsoCode
            );
          },
        },
        {
          field: "unitCost",
          header: "Unit Cost",
          visible: true,
          readonly: false,
          frozen: false,
          dataType: DataType.number,
          dataOptions: [],
          dataTypeOptions: { prefix: this.moneyPrefix.bind(this) },
          filterType: FilterType.contains,
          isNotSortable: true,
          displayFn: (rowData: InventoryDocLine) => {
            return this.normalizerService.currencyNormalizer(
              rowData.unitCost,
              rowData.docReference.doc.currencyIsoCode
            );
          },
        },
        {
          field: "convertedUnitCost",
          header: "Converted Unit Cost",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.number,
          dataOptions: [],
          dataTypeOptions: { prefix: this.zoneMoneyPrefix.bind(this) },
          filterType: FilterType.contains,
          isNotSortable: true,
          displayFn: (rowData: InventoryDocLine) => {
            return this.normalizerService.currencyNormalizer(
              rowData.convertedUnitCost,
              rowData.docReference.zone.defaultCurrencyIsoCode
            );
          },
        },
        {
          field: "subTotal",
          header: "Sub-Total",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.number,
          dataOptions: [],
          dataTypeOptions: { prefix: this.zoneMoneyPrefix.bind(this) },
          filterType: FilterType.contains,
          isNotSortable: true,
          displayFn: (rowData: InventoryDocLine) => {
            return this.normalizerService.currencyNormalizer(
              rowData.subTotal,
              rowData.docReference.zone.defaultCurrencyIsoCode
            );
          },
        },
        {
          field: "allocatedDiscountType",
          header: "Discount Type",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.input,
          dataOptions: [],
          dataTypeOptions: { suffix: this.discountSuffix.bind(this) },
          filterType: FilterType.none,
          isNotSortable: true,
        },
        {
          field: "totalDiscountAmount",
          header: "Allocated Discount",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.number,
          dataOptions: [],
          dataTypeOptions: { prefix: this.moneyPrefix.bind(this) },
          filterType: FilterType.none,
          isNotSortable: true,
          displayFn: (rowData: InventoryDocLine) => {
            return this.normalizerService.currencyNormalizer(
              rowData.totalDiscountAmount,
              rowData.docReference.doc.currencyIsoCode
            );
          },
        },
        {
          field: "convertedTotalDiscountAmount",
          header: "Converted Discount",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.number,
          dataOptions: [],
          dataTypeOptions: { prefix: this.zoneMoneyPrefix.bind(this) },
          filterType: FilterType.none,
          isNotSortable: true,
          displayFn: (rowData: InventoryDocLine) => {
            return this.normalizerService.currencyNormalizer(
              rowData.convertedTotalDiscountAmount,
              rowData.docReference.zone.defaultCurrencyIsoCode
            );
          },
        },
        {
          field: "allocatedDiscountAmount",
          header: "",
          visible: false,
          readonly: true,
          frozen: false,
          dataType: DataType.number,
          dataOptions: [],
          dataTypeOptions: [],
          filterType: FilterType.none,
          isNotSortable: true,
          colWidth: 0,
        },
        {
          field: "allocatedShipperChargeAmount",
          header: "Allocated Freight",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.number,
          dataOptions: [],
          dataTypeOptions: { prefix: this.moneyPrefix.bind(this) },
          filterType: FilterType.contains,
          isNotSortable: true,
          displayFn: (rowData: InventoryDocLine) => {
            return this.normalizerService.currencyNormalizer(
              rowData.allocatedShipperChargeAmount,
              rowData.docReference.doc.currencyIsoCode
            );
          },
        },
        {
          field: "convertedAllocatedShipperChargeAmount",
          header: "Converted Freight",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.number,
          dataOptions: [],
          dataTypeOptions: { prefix: this.zoneMoneyPrefix.bind(this) },
          filterType: FilterType.contains,
          isNotSortable: true,
          displayFn: (rowData: InventoryDocLine) => {
            return this.normalizerService.currencyNormalizer(
              rowData.convertedAllocatedShipperChargeAmount,
              rowData.docReference.zone.defaultCurrencyIsoCode
            );
          },
        },
        {
          field: "totalTax",
          header: "Total Tax",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.number,
          dataOptions: [],
          dataTypeOptions: { prefix: this.zoneMoneyPrefix.bind(this) },
          filterType: FilterType.contains,
          isNotSortable: true,
          displayFn: (rowData: InventoryDocLine) => {
            return this.normalizerService.currencyNormalizer(
              rowData.totalTax,
              rowData.docReference.zone.defaultCurrencyIsoCode
            );
          },
        },
        {
          field: "totalCost",
          header: "Total Cost",
          visible: true,
          readonly: true,
          frozen: false,
          dataType: DataType.number,
          dataOptions: [],
          dataTypeOptions: { prefix: this.zoneMoneyPrefix.bind(this) },
          filterType: FilterType.contains,
          isNotSortable: true,
          displayFn: (rowData: InventoryDocLine) => {
            return this.normalizerService.currencyNormalizer(
              rowData.totalCost,
              rowData.docReference.zone.defaultCurrencyIsoCode
            );
          },
        },
        {
          field: "invoiceNo",
          header: "Invoice #",
          visible: true,
          readonly: false,
          frozen: false,
          dataType: DataType.input,
          dataOptions: [],
          filterType: FilterType.contains,
          isNotSortable: true,
        },
        {
          field: "expiryDate",
          header: "Expiry Date",
          visible: true,
          readonly: false,
          frozen: false,
          dataType: DataType.date_date_only,
          dataOptions: [],
          filterType: FilterType.contains,
          isNotSortable: true,
        },
        {
          field: "lotNo",
          header: "Lot #",
          visible: true,
          readonly: false,
          frozen: false,
          dataType: DataType.input,
          dataOptions: [],
          filterType: FilterType.contains,
          isNotSortable: true,
        },
      ]
    );

    return cols;
  }
}

/* © 2018-2022 TakuLabs Ltd. All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential */
